import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Share2, Link2, Check } from "lucide-react";
import { FaWhatsapp, FaFacebookF, FaPinterestP, FaXTwitter, FaEnvelope } from "react-icons/fa6";
import { SITE_URL } from "../data/seo";

const API = process.env.REACT_APP_BACKEND_URL;

const absolute = (u) => (u && u.startsWith("/") ? SITE_URL + u : u);

const recordShare = (slug, platform) => {
  axios.post(`${API}/api/books/${slug}/share`, { platform }).catch(() => {});
};

export default function ShareButton({ book, variant = "button", className = "" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  const url = `${SITE_URL}/books/${book.slug}`;
  const title = book.title || book.shortTitle || "Digsby Early Learning";
  const text = book.tagline || book.shortDescription || book.description || title;
  const media = absolute(book.image);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const targets = [
    { key: "whatsapp", label: "WhatsApp", Icon: FaWhatsapp, color: "#25D366", href: `https://wa.me/?text=${encodeURIComponent(text + " " + url)}` },
    { key: "facebook", label: "Facebook", Icon: FaFacebookF, color: "#1877F2", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
    { key: "pinterest", label: "Pinterest", Icon: FaPinterestP, color: "#BD081C", href: `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&media=${encodeURIComponent(media || "")}&description=${encodeURIComponent(text)}` },
    { key: "x", label: "X", Icon: FaXTwitter, color: "#000000", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}` },
    { key: "email", label: "Email", Icon: FaEnvelope, color: "#555555", href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text + "\n\n" + url)}` },
  ];

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url });
        recordShare(book.slug, "native");
      } catch (err) {
        console.error("Native share failed or was cancelled:", err);
      }
      return;
    }
    setOpen((o) => !o);
  };

  const openTarget = (t, e) => {
    e.preventDefault();
    e.stopPropagation();
    recordShare(book.slug, t.key);
    window.open(t.href, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const copyLink = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      recordShare(book.slug, "copy");
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy link.");
    }
  };

  const trigger =
    variant === "icon" ? (
      <button
        type="button"
        onClick={handleClick}
        data-testid={`share-icon-${book.slug}`}
        aria-label={`Share ${title}`}
        title="Share"
        className="w-10 h-10 flex items-center justify-center rounded-full bg-white/90 backdrop-blur border-2 border-black/10 text-slate-700 shadow-sm hover:bg-[#FBEF14] hover:-translate-y-0.5 transition-all duration-200"
      >
        <Share2 size={18} />
      </button>
    ) : (
      <button
        type="button"
        onClick={handleClick}
        data-testid={`share-button-${book.slug}`}
        className="inline-flex items-center justify-center gap-2 min-h-[56px] rounded-full bg-white border-2 border-black text-black font-bold text-lg px-8 hover:bg-[#FBEF14] hover:-translate-y-1 transition-all duration-200"
      >
        <Share2 size={20} /> Share
      </button>
    );

  return (
    <div className={variant === "icon" ? className : `relative inline-block ${className}`} ref={ref}>
      {trigger}
      {open && (
        <div
          data-testid={`share-menu-${book.slug}`}
          className="absolute right-0 mt-2 z-50 w-52 rounded-2xl border-2 border-black/10 bg-white shadow-xl p-2"
        >
          {targets.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={(e) => openTarget(t, e)}
              data-testid={`share-${t.key}-${book.slug}`}
              className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-700 hover:bg-[#FFFBF2] transition-colors"
            >
              <span className="w-7 h-7 flex items-center justify-center rounded-full text-white shrink-0" style={{ backgroundColor: t.color }}>
                <t.Icon size={14} />
              </span>
              {t.label}
            </button>
          ))}
          <button
            type="button"
            onClick={copyLink}
            data-testid={`share-copy-${book.slug}`}
            className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-700 hover:bg-[#FFFBF2] transition-colors border-t border-black/5 mt-1 pt-3"
          >
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-[#0000FF] text-white shrink-0">
              {copied ? <Check size={14} /> : <Link2 size={14} />}
            </span>
            {copied ? "Copied!" : "Copy Link"}
          </button>
        </div>
      )}
    </div>
  );
}
